import { Group } from '@visx/group';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { useMount } from '@simplywallst/ui-core';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { ContainerWithPlaceHolder, SankeyWrapper } from './styled';
import { retriableCalcSankeyData } from './hooks/useSankey';
import { useTheme } from '@simplywallst/ui-core';
import { SankeyChart } from './SankeyChart';
import type { CurrencyISO } from '@/constants/currencies';
import Media from 'react-media';
import Slider from '../Slider';
import {
  getEndDateByYear,
  getTodayDate,
  getYearArrayFromTimeseries,
  getYearByDate,
} from './utils/utils';
import type { CompanyTimeSeries } from '../../../../hooks/useCompanyTimeSeries';
import {
  mapGqlDataToRawData,
  isInsufficientData as checkInsufficientData,
} from './hooks/utils';
import { useTracking } from 'react-tracking';
import debounce from 'lodash/debounce';

interface Props {
  currencyISO: CurrencyISO;
  gqlData?: CompanyTimeSeries;
  labels: ComponentProps<typeof SankeyChart>['labels'] & {
    insufficientData: string;
  };
}

export const Sankey = ({ currencyISO, gqlData, labels }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const { trackEvent } = useTracking({
    subject: 'past',
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const chartPadding = isMobileScreen
    ? { top: 10, left: 5, right: 5, bottom: 50 }
    : { top: 60, left: 5, right: 5, bottom: 25 };

  const theme = useTheme();

  const initChartWidth = 580;

  const [endDate, setEndDate] = useState<string>(getTodayDate());

  const [chartWidth, setChartWidth] = useState(initChartWidth);

  const chartHeight = isMobileScreen ? 280 : 500;
  const initWidth = chartWidth - chartPadding.left - chartPadding.right;
  const initHeight = chartHeight - chartPadding.top - chartPadding.bottom;

  // Update chart width when the container element is mounted
  useMount(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      const widthWithBound = Math.max(width, initChartWidth);
      setChartWidth(widthWithBound);
    }
  });

  const rawData = mapGqlDataToRawData(gqlData, endDate);
  const { data: sankeyData, chartHeight: calculatedChartHeight } =
    retriableCalcSankeyData({
      rawData,
      width: initWidth,
      height: initHeight,
      isMobileScreen,
    });
  const sliderHeight = 40;
  const chartPaddingTopRaw =
    chartPadding.top +
    (chartHeight -
      chartPadding.top -
      chartPadding.bottom -
      calculatedChartHeight) /
      2;

  const chartPaddingTop = isMobileScreen
    ? chartPaddingTopRaw / 2 + sliderHeight
    : chartPaddingTopRaw;

  const timeSeries = gqlData?.timeSeries;
  const yearArray = getYearArrayFromTimeseries(timeSeries);
  const marks: [number, string][] = yearArray.map((i) => [i, i.toString()]);

  const debouncedTrackEvent = useMemo(
    () => debounce(trackEvent, 5000, { trailing: true }),
    [trackEvent]
  );

  const onScroll = useCallback(
    () =>
      debouncedTrackEvent({
        modifier: 'fundamentals_sankey_chart',
        action: 'scroll',
      }),
    [debouncedTrackEvent]
  );

  const handleSlideBarChange = useCallback(
    (value: number) => {
      setEndDate(getEndDateByYear(value));
      trackEvent({
        modifier: 'fundamentals_sankey_slider',
        action: 'change',
      });
    },
    [setEndDate, trackEvent]
  );

  const isInsufficientData = checkInsufficientData(rawData);

  return (
    <>
      <Media
        query={{ minWidth: theme.lgMinRaw }}
        render={() => (
          <Slider
            marks={marks}
            defaultValue={getYearByDate(endDate)}
            onChange={(value) => handleSlideBarChange(value)}
            timeSeries={timeSeries}
          />
        )}
      />

      {isInsufficientData ? (
        <ContainerWithPlaceHolder placeItems="center">
          <div className="text-sm">{labels.insufficientData}</div>
        </ContainerWithPlaceHolder>
      ) : (
        <SankeyWrapper
          ref={containerRef}
          id="revenue-and-expenses-breakdown-chart-loaded-content"
          onScroll={onScroll}
        >
          <svg width={chartWidth} height={chartHeight}>
            <Group top={chartPaddingTop} left={chartPadding.left}>
              <SankeyChart
                isMobileScreen={isMobileScreen}
                sankeyData={sankeyData}
                currencyISO={currencyISO}
                rawData={rawData}
                labels={labels}
              />
            </Group>
          </svg>
        </SankeyWrapper>
      )}

      <Media
        query={{ maxWidth: theme.mdMaxRaw }}
        render={() => (
          <Slider
            marks={marks}
            defaultValue={getYearByDate(endDate)}
            onChange={(value) => handleSlideBarChange(value)}
            timeSeries={timeSeries}
          />
        )}
      />
    </>
  );
};
